"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type { AllergenOption } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

/** Canvas's `/Allergen` endpoint needs a non-trivial query; mirror its floor. */
export const MIN_ALLERGEN_QUERY_LENGTH = 2;

interface SearchAllergensQueryVariables {
  text: string;
}

interface SearchAllergensQueryData {
  searchAllergens: AllergenOption[];
}

export const searchAllergensQuery = gql`
  query searchAllergens($text: String!) {
    searchAllergens(text: $text) {
      fdbCode
      rxnormCode
      display
    }
  }
`.loc?.source.body as GeneratedQuery<
  SearchAllergensQueryVariables,
  SearchAllergensQueryData
>;

interface UseSearchAllergensOptions {
  /** The (already debounced) search text from the autocomplete input. */
  text: string;
  enabled?: boolean;
}

/**
 * Typeahead over FDB allergen catalog; each option carries the FDB coding for a
 * structured AllergyIntolerance. Pass debounced text; idle until min length.
 */
export const useSearchAllergens = ({
  text,
  enabled = true,
}: UseSearchAllergensOptions) => {
  const trimmed = text.trim();
  return useQuery<AllergenOption[]>({
    queryKey: ["allergens", trimmed],
    queryFn: async () => {
      const response = await fetchGraphQLClient(searchAllergensQuery, {
        text: trimmed,
      });
      return response.data?.searchAllergens ?? [];
    },
    enabled: enabled && trimmed.length >= MIN_ALLERGEN_QUERY_LENGTH,
    // The FDB catalog is effectively static for a session — don't refetch.
    staleTime: 5 * 60 * 1000,
  });
};
